import type { FunctionComponent, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { sectionStoryBlockHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/sectionStoryBlockHomeV2QueueItemsEdge.graphql";

import {
  ArticleOneRow,
  BottomDivider,
  BottomItem,
  BottomRow,
  Container,
  FullDivider,
  HeaderBlock,
  HeroImageLink,
  PrimaryText,
  SecondaryText,
  StyedContentCoverImage,
  StyledPrimaryItem,
  StyledSecondaryItem,
} from "./styles";

type Props = {
  className?: string;
  header: ReactNode;
  onClick?: (
    entityId?: string,
    urlAlias?: string,
    isSponsorArticle?: boolean,
    index?: number,
  ) => void;
  reference: sectionStoryBlockHomeV2QueueItemsEdge$key;
  withSection?: boolean;
};

export const HomeV2SectionStoryBlock: FunctionComponent<Props> = ({
  className,
  header,
  onClick,
  reference,
  withSection = true,
}) => {
  const edges = useFragment(
    graphql`
      fragment sectionStoryBlockHomeV2QueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            urlAlias
            ...contentCoverImageContent @arguments(withSize1200x800: true, withSize540x360: true)
            ...homeSecondaryV2ContentItemContent
            ...entityLink
          }
        }
      }
    `,
    reference,
  );

  const nodes = (edges ?? [])
    .map(edge => edge.node)
    .filter(notEmpty)
    .filter(node => notEmpty(node.entityId))
    .slice(0, 4);

  if (nodes.length === 0) return null;

  const [primary, secondary, third, fourth] = nodes;

  return (
    <Container className={className}>
      <HeaderBlock>{header}</HeaderBlock>

      {primary && (
        <ArticleOneRow>
          <PrimaryText>
            <StyledPrimaryItem
              isReadingTimeEnabled
              onClick={(entityId, urlAlias) => onClick?.(entityId, urlAlias, false, 0)}
              reference={primary}
              withComment
              withSection={withSection}
              withSummary
            />
          </PrimaryText>
          <HeroImageLink
            onClick={() => onClick?.(primary.entityId, primary.urlAlias, false, 0)}
            reference={primary}
          >
            <StyedContentCoverImage
              reference={primary}
              responsiveVariants={{
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              }}
            />
          </HeroImageLink>
          {secondary && (
            <SecondaryText>
              <StyledSecondaryItem
                isReadingTimeEnabled
                onClick={(entityId, urlAlias) => onClick?.(entityId, urlAlias, false, 1)}
                reference={secondary}
                withComment
                withSection={withSection}
              />
            </SecondaryText>
          )}
        </ArticleOneRow>
      )}

      {(third || fourth) && (
        <>
          <FullDivider />
          <BottomRow>
            {third && (
              <BottomItem>
                <StyledSecondaryItem
                  isReadingTimeEnabled
                  onClick={(entityId, urlAlias) => onClick?.(entityId, urlAlias, false, 2)}
                  reference={third}
                  withComment
                  withSection={withSection}
                />
              </BottomItem>
            )}
            {third && fourth && <BottomDivider />}
            {fourth && (
              <BottomItem>
                <StyledSecondaryItem
                  isReadingTimeEnabled
                  onClick={(entityId, urlAlias) => onClick?.(entityId, urlAlias, false, 3)}
                  reference={fourth}
                  withComment
                  withSection={withSection}
                />
              </BottomItem>
            )}
          </BottomRow>
        </>
      )}
    </Container>
  );
};

HomeV2SectionStoryBlock.displayName = "HomeV2SectionStoryBlock";
